import { lazy, Suspense, createContext, useContext, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { supabase } from './supabase'
import MarketingLayout from './components/layouts/MarketingLayout'
import AppLayout from './components/layouts/AppLayout'
import RouteSkeleton from './components/RouteSkeleton'
import RouteFocus from './components/RouteFocus'
import { pageview } from './lib/analytics'

// ── Lazy routes ────────────────────────────────────────────
const Home           = lazy(() => import('./pages/Home'))
const Pricing        = lazy(() => import('./pages/Pricing'))
const Security       = lazy(() => import('./pages/Security'))
const Legal          = lazy(() => import('./pages/Legal'))
const Login          = lazy(() => import('./pages/Login'))
const Signup         = lazy(() => import('./pages/Signup'))
const Reset          = lazy(() => import('./pages/Reset'))
const Callback       = lazy(() => import('./pages/Callback'))
const UpdatePassword = lazy(() => import('./pages/UpdatePassword'))
const Dashboard      = lazy(() => import('./pages/Dashboard'))
const Onboarding     = lazy(() => import('./pages/Onboarding'))
const Generator      = lazy(() => import('./pages/Generator'))
const Billing        = lazy(() => import('./pages/Billing'))
const Settings       = lazy(() => import('./pages/Settings'))
const History        = lazy(() => import('./pages/History'))
const NotFound       = lazy(() => import('./pages/NotFound'))

// ── Auth context ───────────────────────────────────────────
export const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      signUp: (email, password, meta) =>
        supabase.auth.signUp({ email, password, options: { data: meta } }),
      signIn: (email, password) =>
        supabase.auth.signInWithPassword({ email, password }),
      signOut: () => supabase.auth.signOut(),
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// Layout route that guards the entire /app tree
function ProtectedRoute() {
  const { user, loading } = useAuth()
  if (loading) return <RouteSkeleton />
  if (!user)   return <Navigate to="/login" replace />
  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Outlet />
    </>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function PageviewTracker() {
  const { pathname, search } = useLocation()
  useEffect(() => { pageview() }, [pathname, search])
  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
          <ScrollToTop />
          <RouteFocus />
          <PageviewTracker />
          <Suspense fallback={<RouteSkeleton />}>
            <Routes>
              {/* ── Marketing & auth routes ── */}
              <Route element={<MarketingLayout />}>
                <Route path="/"                     element={<Home />} />
                <Route path="/pricing"              element={<Pricing />} />
                <Route path="/security"             element={<Security />} />
                <Route path="/legal"                element={<Legal />} />
                <Route path="/login"                element={<Login />} />
                <Route path="/signup"               element={<Signup />} />
                <Route path="/reset"                element={<Reset />} />
                <Route path="/auth/callback"        element={<Callback />} />
                <Route path="/auth/update-password" element={<UpdatePassword />} />
                <Route path="/terms"                element={<Navigate to="/legal" replace />} />
              </Route>

              {/* ── Protected app routes ── */}
              <Route element={<ProtectedRoute />}>
                <Route element={<AppLayout />}>
                  <Route path="/app/dashboard"        element={<Dashboard />} />
                  <Route path="/app/history"          element={<History />} />
                  <Route path="/app/onboarding/:step" element={<Onboarding />} />
                  <Route path="/app/grants/:id"       element={<Generator />} />
                  <Route path="/app/billing"          element={<Billing />} />
                  <Route path="/app/settings"         element={<Settings />} />
                </Route>
              </Route>

              {/* ── Legacy route redirects ── */}
              <Route path="/dashboard" element={<Navigate to="/app/dashboard"  replace />} />
              <Route path="/generator" element={<Navigate to="/app/grants/new" replace />} />
              <Route path="/history"   element={<Navigate to="/app/history"    replace />} />
              <Route path="/setup"     element={<Navigate to="/app/settings"   replace />} />
              <Route path="/settings"  element={<Navigate to="/app/settings"   replace />} />

              {/* ── 404 ── */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*"    element={<Navigate to="/404" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
    </BrowserRouter>
  )
}
